import JSZip from 'jszip';
import { Project, AgentResult } from '../types';
import { MemoryService } from './memoryService';

export class ProjectExportService {
  /**
   * Export a project + its results + memories as a ZIP file
   */
  static async exportAsZip(
    project: Project,
    results: AgentResult[]
  ): Promise<void> {
    const zip = new JSZip();

    // 1. Project data
    zip.file('project.json', JSON.stringify(project, null, 2));

    // 2. Agent results
    zip.file('results.json', JSON.stringify(results, null, 2));

    // 3. Company memories — collect from all companies in customData
    const memories: Record<string, any> = {};
    if (project.customData) {
      project.customData.forEach((company: any) => {
        if (company.id) {
          const mem = MemoryService.getMemory(company.id, project.id);
          if (mem) memories[company.id] = mem;
        }
      });
    }
    zip.file('memories.json', JSON.stringify(memories, null, 2));

    // 4. Metadata
    zip.file('meta.json', JSON.stringify({
      exportDate: new Date().toISOString(),
      projectName: project.name,
      version: '1.0',
      format: 'bdat-project-export'
    }, null, 2));

    // Generate & download
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/[^a-zA-Z0-9]/g, '_')}_BDAT_Export.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Import a project from a ZIP file
   * Returns { project, results, memories } that the caller can merge into state
   */
  static async importFromZip(file: File): Promise<{
    project: Project;
    results: AgentResult[];
    memories: Record<string, any>;
  }> {
    const zip = await JSZip.loadAsync(file);

    const projectFile = zip.file('project.json');
    if (!projectFile) throw new Error('Ongeldig ZIP bestand: project.json ontbreekt');

    const project: Project = JSON.parse(await projectFile.async('string'));

    let results: AgentResult[] = [];
    const resultsFile = zip.file('results.json');
    if (resultsFile) {
      results = JSON.parse(await resultsFile.async('string'));
    }

    let memories: Record<string, any> = {};
    const memoriesFile = zip.file('memories.json');
    if (memoriesFile) {
      memories = JSON.parse(await memoriesFile.async('string'));
    }

    // Assign new ID to avoid collisions
    project.id = crypto.randomUUID();

    return { project, results, memories };
  }
}
